import React, { useMemo, useRef, useState } from 'react';
import type {
  ArArticleDetail,
  ArImage,
  ArSection,
  ArSectionType,
} from '../../growth-articles/types';
import { AR_ARTICLE_CREATE_DRAFT, AR_ARTICLE_DETAILS } from '../../growth-articles/mockData';
import { Icon } from '../Icon/Icon';
import './growth-articles.css';

const DEFAULT_ARTICLE_ID = 'dental-implants-guide';

const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const editFieldClass =
  'w-full rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-2 text-[13px] text-[var(--ink-1)] outline-none transition focus:border-[var(--ai-4)] focus:shadow-[0_0_0_3px_rgba(91,141,239,0.15)]';

const SECTION_ADD_OPTIONS: { type: ArSectionType; label: string }[] = [
  { type: 'h1', label: 'H1' },
  { type: 'h2', label: 'H2' },
  { type: 'p', label: 'Paragraph' },
  { type: 'list', label: 'List' },
  { type: 'table', label: 'Table' },
  { type: 'image', label: 'Image' },
  { type: 'cta', label: 'CTA' },
];

const IMAGE_TYPES: ArImage['type'][] = ['hero', 'comparison', 'before-after', 'infographic'];

function articleStatusLabel(status: ArArticleDetail['status']): string {
  return status === 'ready' ? 'Ready to Publish' : 'Draft';
}

function emptySection(type: ArSectionType, imageIdx?: number): ArSection {
  if (type === 'list') return { type, items: [''] };
  if (type === 'table') return { type, headers: ['Column A', 'Column B'], rows: [['', '']] };
  if (type === 'image') return { type, imageIdx: imageIdx ?? 0 };
  return { type, text: '' };
}

function countWords(article: ArArticleDetail): number {
  const parts: string[] = [article.title, article.meta.title, article.meta.description];
  for (const section of article.sections) {
    if (section.text) parts.push(section.text);
    if (section.items?.length) parts.push(...section.items);
    if (section.headers?.length) parts.push(...section.headers);
    if (section.rows?.length) {
      for (const row of section.rows) parts.push(...row);
    }
  }
  return parts
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ArCopyButton({ label = 'Copy', onClick }: { label?: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 text-[12px] font-medium text-[var(--ink-3)]"
    >
      <Icon kind="copy" size={13} stroke={1.9} />
      {label}
    </button>
  );
}

function buildFullArticleText(article: ArArticleDetail): string {
  const blocks = article.sections.map((section) => {
    if (section.text) return section.text;
    if (section.items?.length) return section.items.map((item) => `- ${item}`).join('\n');
    if (section.rows?.length) return section.rows.map((row) => row.join('\t')).join('\n');
    return '';
  });
  return [article.title, article.meta.title, article.meta.description, ...blocks]
    .filter(Boolean)
    .join('\n\n');
}

async function copyToClipboard(
  text: string,
  onCopyText?: (text: string) => void | Promise<void>,
): Promise<void> {
  if (onCopyText) {
    await onCopyText(text);
    return;
  }
  await navigator.clipboard.writeText(text);
}

function ArSectionImage({ image }: { image: ArImage }) {
  return (
    <div className="my-6 overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--ai-tint)]">
      {image.url ? (
        <img
          src={image.url}
          alt={image.alt || image.name}
          className="max-h-[280px] w-full object-cover"
        />
      ) : (
        <div className="flex h-[200px] items-center justify-center gap-2 text-[var(--ai-3)]">
          <Icon kind="image" size={22} stroke={1.6} />
          <span className="text-[13px] font-medium">{image.name}</span>
        </div>
      )}
      <div className="border-t border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-2 text-[12px] italic text-[var(--ink-3)]">
        Alt text: {image.alt || '—'}
      </div>
    </div>
  );
}

function ArArticleSection({ section, images }: { section: ArSection; images: ArImage[] }) {
  if (section.type === 'h1') {
    return (
      <h1 className="m-0 mb-5 font-[var(--font-display)] text-[28px] font-semibold leading-[1.3] tracking-[-0.02em] text-[var(--ink-0)]">
        {section.text}
      </h1>
    );
  }
  if (section.type === 'h2') {
    return (
      <h2 className="mb-3 mt-9 font-[var(--font-display)] text-[20px] font-semibold leading-[1.3] tracking-[-0.015em] text-[var(--ink-0)]">
        {section.text}
      </h2>
    );
  }
  if (section.type === 'p') {
    return <p className="mb-4 text-[15px] leading-[1.8] text-[var(--ink-2)]">{section.text}</p>;
  }
  if (section.type === 'list') {
    return (
      <ul className="mb-4 list-disc space-y-1.5 pl-5">
        {(section.items ?? []).map((item) => (
          <li key={item} className="text-[15px] leading-[1.8] text-[var(--ink-2)]">
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (section.type === 'table') {
    return (
      <div className="my-5 overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)]">
        <table className="w-full border-collapse text-left text-[14px]">
          <thead>
            <tr>
              {(section.headers ?? []).map((header) => (
                <th
                  key={header}
                  className="border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-3)]"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(section.rows ?? []).map((row) => (
              <tr key={row.join('|')}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${row.join('|')}-${String(cellIndex)}`}
                    className={[
                      'border-b border-[var(--cream-2)] px-3.5 py-2.5',
                      cellIndex === 1
                        ? 'font-[var(--font-display)] font-semibold text-[var(--success)]'
                        : 'text-[var(--ink-2)]',
                    ].join(' ')}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (section.type === 'image' && typeof section.imageIdx === 'number') {
    const image = images[section.imageIdx];
    return image ? <ArSectionImage image={image} /> : null;
  }
  if (section.type === 'cta') {
    return (
      <div className="mt-8 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[var(--success-tint)] px-6 py-6 text-center">
        <p className="m-0 mb-4 text-[16px] font-medium leading-[1.6] text-[var(--ink-1)]">{section.text}</p>
        <span className="inline-flex items-center rounded-[var(--r-sm)] bg-[var(--success)] px-7 py-3 text-[15px] font-semibold text-[var(--cream-1)]">
          Book Your Free Consultation
        </span>
      </div>
    );
  }
  return null;
}

function ArMetaRow({
  label,
  value,
  onCopy,
  editable,
  onChange,
}: {
  label: string;
  value: string;
  onCopy?: (text: string) => void;
  editable?: boolean;
  onChange?: (value: string) => void;
}) {
  return (
    <div className="mb-3.5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className={eyebrowClass}>{label}</span>
        {!editable ? (
          <ArCopyButton
            onClick={() => {
              onCopy?.(value);
            }}
          />
        ) : null}
      </div>
      {editable ? (
        <input
          className={editFieldClass}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          placeholder={label}
        />
      ) : (
        <div className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-2 font-[var(--font-mono)] text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
          {value}
        </div>
      )}
    </div>
  );
}

const IMAGE_TONE: Record<ArImage['type'], string> = {
  hero: 'var(--success)',
  comparison: 'var(--info)',
  'before-after': 'var(--success)',
  infographic: 'var(--gold)',
};

function ArImageCard({
  image,
  onDownload,
  editable,
  onRemove,
  onAltChange,
  onTypeChange,
}: {
  image: ArImage;
  onDownload?: (imageId: number) => void;
  editable?: boolean;
  onRemove?: () => void;
  onAltChange?: (alt: string) => void;
  onTypeChange?: (type: ArImage['type']) => void;
}) {
  const accent = IMAGE_TONE[image.type];
  return (
    <article className="overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)]">
      <div
        className="relative flex h-[88px] flex-col items-center justify-center gap-1.5 border-b border-[var(--cream-2)]"
        style={{ background: `color-mix(in srgb, ${accent} 10%, transparent)`, color: accent }}
      >
        {image.url ? (
          <img src={image.url} alt={image.alt || image.name} className="h-full w-full object-cover" />
        ) : (
          <>
            <Icon kind="image" size={20} stroke={1.6} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em]">{image.type}</span>
          </>
        )}
      </div>
      <div className="p-2.5">
        <p className="truncate text-[12.5px] font-medium text-[var(--ink-1)]">{image.name}</p>
        <p className="mt-0.5 text-[11px] text-[var(--ink-4)]">
          {image.size}
          {image.dimensions !== '—' ? ` · ${image.dimensions}` : ''}
        </p>
        {editable ? (
          <div className="mt-2 space-y-1.5">
            <select
              className={editFieldClass}
              value={image.type}
              onChange={(event) => onTypeChange?.(event.target.value as ArImage['type'])}
            >
              {IMAGE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <input
              className={editFieldClass}
              value={image.alt}
              onChange={(event) => onAltChange?.(event.target.value)}
              placeholder="Alt text…"
            />
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2 text-[12px] font-medium text-[var(--terracotta)]"
            >
              <Icon kind="trash" size={13} stroke={2} />
              Remove
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onDownload?.(image.id)}
            className="mt-2 inline-flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-2 text-[12px] font-semibold text-[var(--cream-1)]"
          >
            <Icon kind="download" size={13} stroke={2} />
            Download
          </button>
        )}
      </div>
    </article>
  );
}

function CreateSectionEditor({
  section,
  index,
  images,
  canMoveUp,
  canMoveDown,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onPickImage,
}: {
  section: ArSection;
  index: number;
  images: ArImage[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (section: ArSection) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPickImage: () => void;
}) {
  return (
    <div className="mb-4 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <span className={`${eyebrowClass} m-0`}>
          Block {index + 1} · {section.type.toUpperCase()}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Move up"
          >
            <Icon kind="up" size={13} stroke={2} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Move down"
          >
            <Icon kind="down" size={13} stroke={2} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--terracotta)]"
            aria-label="Remove block"
          >
            <Icon kind="trash" size={13} stroke={2} />
          </button>
        </div>
      </div>

      {section.type === 'h1' || section.type === 'h2' || section.type === 'p' || section.type === 'cta' ? (
        <textarea
          className={editFieldClass}
          rows={section.type === 'p' || section.type === 'cta' ? 4 : 2}
          value={section.text ?? ''}
          onChange={(event) => onChange({ ...section, text: event.target.value })}
          placeholder={
            section.type === 'h1'
              ? 'Headline…'
              : section.type === 'h2'
                ? 'Section heading…'
                : section.type === 'cta'
                  ? 'Call to action…'
                  : 'Body paragraph…'
          }
        />
      ) : null}

      {section.type === 'list' ? (
        <textarea
          className={editFieldClass}
          rows={5}
          value={(section.items ?? []).join('\n')}
          onChange={(event) =>
            onChange({
              ...section,
              items: event.target.value.split('\n'),
            })
          }
          placeholder={'One list item per line…'}
        />
      ) : null}

      {section.type === 'table' ? (
        <div className="space-y-2">
          <input
            className={editFieldClass}
            value={(section.headers ?? []).join(', ')}
            onChange={(event) =>
              onChange({
                ...section,
                headers: event.target.value.split(',').map((cell) => cell.trim()),
              })
            }
            placeholder="Headers: Col A, Col B"
          />
          <textarea
            className={editFieldClass}
            rows={4}
            value={(section.rows ?? []).map((row) => row.join(', ')).join('\n')}
            onChange={(event) =>
              onChange({
                ...section,
                rows: event.target.value
                  .split('\n')
                  .filter((line) => line.trim().length > 0)
                  .map((line) => line.split(',').map((cell) => cell.trim())),
              })
            }
            placeholder={'Rows — one per line:\nValue A, Value B'}
          />
        </div>
      ) : null}

      {section.type === 'image' ? (
        <div className="space-y-2">
          {typeof section.imageIdx === 'number' && images[section.imageIdx] ? (
            <ArSectionImage image={images[section.imageIdx]!} />
          ) : (
            <p className="m-0 text-[12.5px] text-[var(--ink-3)]">No image selected for this block.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onPickImage}
              className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-3 text-[12.5px] font-semibold text-[var(--cream-1)]"
            >
              <Icon kind="upload" size={14} stroke={2} />
              Upload image
            </button>
            {images.length > 0 ? (
              <select
                className={editFieldClass}
                style={{ width: 'auto', minWidth: 160 }}
                value={section.imageIdx ?? 0}
                onChange={(event) =>
                  onChange({ ...section, imageIdx: Number(event.target.value) })
                }
              >
                {images.map((image, imageIndex) => (
                  <option key={image.id} value={imageIndex}>
                    {image.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export type ArticlePreviewMode = 'view' | 'create';

export interface ArticlePreviewViewProps {
  articleId?: string;
  article?: ArArticleDetail;
  loading?: boolean;
  mode?: ArticlePreviewMode;
  saving?: boolean;
  onBack?: () => void;
  onCopy?: () => void;
  onCopyText?: (text: string) => void | Promise<void>;
  onDownload?: () => void;
  onDownloadImage?: (imageId: number) => void | Promise<void>;
  onHandoff?: () => void;
  onRegenerate?: () => void;
  onSave?: (article: ArArticleDetail) => void | Promise<void>;
}

export function ArticlePreviewView({
  articleId,
  article: articleProp,
  loading = false,
  mode = 'view',
  saving = false,
  onBack,
  onCopy,
  onCopyText,
  onDownload: _onDownload,
  onDownloadImage,
  onHandoff,
  onRegenerate,
  onSave,
}: ArticlePreviewViewProps = {}) {
  const isCreate = mode === 'create';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageTargetRef = useRef<'library' | number>('library');

  const seed = useMemo(
    () =>
      articleProp ??
      (isCreate
        ? AR_ARTICLE_CREATE_DRAFT
        : (AR_ARTICLE_DETAILS[articleId ?? ''] ??
          AR_ARTICLE_DETAILS[DEFAULT_ARTICLE_ID] ??
          Object.values(AR_ARTICLE_DETAILS)[0]!)),
    [articleId, articleProp, isCreate],
  );

  const [draft, setDraft] = useState<ArArticleDetail>(seed);
  const article = isCreate ? draft : (articleProp ?? seed);
  const statusLabel = articleStatusLabel(article.status);
  const displayWordCount = isCreate ? countWords(article) : article.wordCount;

  const handleCopyText = (text: string) => {
    void copyToClipboard(text, onCopyText);
  };

  const handleCopyEntireArticle = () => {
    if (onCopy) {
      onCopy();
      return;
    }
    handleCopyText(buildFullArticleText(article));
  };

  const updateDraft = (patch: Partial<ArArticleDetail>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const updateMeta = (patch: Partial<ArArticleDetail['meta']>) => {
    setDraft((current) => ({ ...current, meta: { ...current.meta, ...patch } }));
  };

  const addSection = (type: ArSectionType) => {
    setDraft((current) => {
      const nextImageIdx =
        type === 'image' ? Math.max(0, current.images.length - 1) : undefined;
      return {
        ...current,
        sections: [...current.sections, emptySection(type, nextImageIdx)],
      };
    });
  };

  const updateSection = (index: number, section: ArSection) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.map((row, i) => (i === index ? section : row)),
    }));
  };

  const removeSection = (index: number) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.filter((_, i) => i !== index),
    }));
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    setDraft((current) => {
      const next = [...current.sections];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      const tmp = next[index]!;
      next[index] = next[target]!;
      next[target] = tmp;
      return { ...current, sections: next };
    });
  };

  const openFilePicker = (target: 'library' | number) => {
    imageTargetRef.current = target;
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (files: FileList | null) => {
    const file = files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      setDraft((current) => {
        const nextId =
          current.images.reduce((max, image) => Math.max(max, image.id), 0) + 1;
        const image: ArImage = {
          id: nextId,
          name: file.name,
          type: 'hero',
          size: formatFileSize(file.size),
          dimensions: '—',
          alt: '',
          url,
        };
        const images = [...current.images, image];
        const imageIdx = images.length - 1;
        const target = imageTargetRef.current;
        const sections =
          typeof target === 'number'
            ? current.sections.map((section, index) =>
                index === target ? { ...section, type: 'image' as const, imageIdx } : section,
              )
            : current.sections;
        return { ...current, images, sections };
      });
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (imageIndex: number) => {
    setDraft((current) => {
      const images = current.images.filter((_, i) => i !== imageIndex);
      const sections: ArSection[] = [];
      for (const section of current.sections) {
        if (section.type !== 'image' || typeof section.imageIdx !== 'number') {
          sections.push(section);
          continue;
        }
        if (section.imageIdx === imageIndex) continue;
        if (section.imageIdx > imageIndex) {
          sections.push({ ...section, imageIdx: section.imageIdx - 1 });
          continue;
        }
        sections.push(section);
      }
      return { ...current, images, sections };
    });
  };

  const canSave =
    Boolean(onSave) &&
    article.targetKeyword.trim().length > 0 &&
    (article.title.trim().length > 0 ||
      article.sections.some(
        (section) =>
          Boolean(section.text?.trim()) ||
          Boolean(section.items?.some((item) => item.trim())) ||
          section.type === 'image' ||
          Boolean(section.rows?.length),
      ));

  return (
    <div className="growth-articles-root relative mx-auto w-full max-w-[1180px] px-[30px] pb-[90px] pt-[26px]">
      {isCreate ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => handleFilesSelected(event.target.files)}
        />
      ) : null}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-1 text-[12px] font-bold text-[var(--success)]">
            {isCreate ? 'New draft' : statusLabel}
          </span>
          <span className="inline-flex items-center rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[12px] font-medium text-[var(--ink-3)]">
            {article.category ?? 'Uncategorized'}
          </span>
          <span className="text-[13px] text-[var(--ink-4)]">
            {displayWordCount.toLocaleString()} words · {article.readTime} · {article.images.length}{' '}
            images
            {loading || saving ? ' · Updating…' : ''}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {isCreate ? (
            <button
              type="button"
              onClick={() => {
                void onSave?.({
                  ...article,
                  wordCount: countWords(article),
                });
              }}
              disabled={!canSave || saving}
              className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2.5 text-[13px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon kind="plus" size={14} stroke={2.2} />
              {saving ? 'Creating…' : 'Create Article'}
            </button>
          ) : (
            <>
              {onRegenerate ? (
                <button
                  type="button"
                  onClick={onRegenerate}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2.5 text-[13px] font-semibold text-[var(--ink-2)]"
                >
                  <Icon kind="refresh" size={14} stroke={1.9} />
                  Regenerate
                </button>
              ) : null}
              {onHandoff ? (
                <button
                  type="button"
                  onClick={onHandoff}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--info)] px-4 py-2.5 text-[13px] font-semibold text-[var(--cream-1)]"
                >
                  <Icon kind="upload" size={14} stroke={1.9} />
                  Hand off to publish
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleCopyEntireArticle}
                className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2.5 text-[13px] font-semibold text-[var(--cream-1)]"
              >
                <Icon kind="copy" size={14} stroke={1.9} />
                Copy Entire Article
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <article className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[clamp(20px,4vw,48px)] py-10 shadow-[var(--sh-1)]">
          {isCreate ? (
            <>
              <div className="mb-5 flex flex-wrap gap-1.5">
                {SECTION_ADD_OPTIONS.map((option) => (
                  <button
                    key={option.type}
                    type="button"
                    onClick={() => addSection(option.type)}
                    className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 text-[12px] font-semibold text-[var(--ink-2)]"
                  >
                    <Icon kind="plus" size={12} stroke={2.2} />
                    {option.label}
                  </button>
                ))}
              </div>

              {article.sections.length === 0 ? (
                <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-10 text-center text-[13px] leading-[1.6] text-[var(--ink-3)]">
                  No blocks yet. Add an H1, paragraph, list, table, image, or CTA to start composing.
                </div>
              ) : (
                article.sections.map((section, index) => (
                  <CreateSectionEditor
                    key={`${section.type}-${String(index)}`}
                    section={section}
                    index={index}
                    images={article.images}
                    canMoveUp={index > 0}
                    canMoveDown={index < article.sections.length - 1}
                    onChange={(next) => updateSection(index, next)}
                    onRemove={() => removeSection(index)}
                    onMoveUp={() => moveSection(index, -1)}
                    onMoveDown={() => moveSection(index, 1)}
                    onPickImage={() => openFilePicker(index)}
                  />
                ))
              )}
            </>
          ) : (
            article.sections.map((section, index) => (
              <ArArticleSection
                key={`${section.type}-${String(index)}`}
                section={section}
                images={article.images}
              />
            ))
          )}
        </article>

        <aside className="space-y-4">
          <div className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-1)]">
            <div className="mb-2.5 flex items-center justify-between gap-2">
              <span className={eyebrowClass}>Article Title</span>
              {!isCreate ? (
                <ArCopyButton
                  onClick={() => {
                    handleCopyText(article.title);
                  }}
                />
              ) : null}
            </div>
            {isCreate ? (
              <input
                className={editFieldClass}
                value={article.title}
                onChange={(event) => updateDraft({ title: event.target.value })}
                placeholder="Article title…"
              />
            ) : (
              <div className="text-[14px] font-semibold leading-[1.5] text-[var(--ink-1)]">
                {article.title}
              </div>
            )}
          </div>

          <div className="rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-1)]">
            <div className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--info)]">
              SEO Meta Tags
            </div>
            <ArMetaRow
              label="Page Title"
              value={article.meta.title}
              onCopy={handleCopyText}
              editable={isCreate}
              onChange={(value) => updateMeta({ title: value })}
            />
            <ArMetaRow
              label="Meta Description"
              value={article.meta.description}
              onCopy={handleCopyText}
              editable={isCreate}
              onChange={(value) => updateMeta({ description: value })}
            />
            <ArMetaRow
              label="URL Slug"
              value={article.meta.slug}
              onCopy={handleCopyText}
              editable={isCreate}
              onChange={(value) => updateMeta({ slug: value })}
            />
          </div>

          <div className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-1)]">
            <div className="mb-3.5 flex items-center justify-between gap-2">
              <span className={eyebrowClass}>Images ({article.images.length})</span>
              {isCreate ? (
                <button
                  type="button"
                  onClick={() => openFilePicker('library')}
                  className="inline-flex h-8 items-center gap-1.5 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-2.5 text-[12px] font-semibold text-[var(--cream-1)]"
                >
                  <Icon kind="upload" size={13} stroke={2} />
                  Upload
                </button>
              ) : (
                <span className="inline-flex h-8 items-center gap-1.5 rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-2.5 text-[12px] font-semibold text-[var(--success)]">
                  <Icon kind="download" size={13} stroke={2} />
                  Download All
                </span>
              )}
            </div>
            {article.images.length ? (
              <div className="grid grid-cols-2 gap-2.5">
                {article.images.map((image, imageIndex) => (
                  <ArImageCard
                    key={image.id}
                    image={image}
                    editable={isCreate}
                    {...(onDownloadImage ? { onDownload: onDownloadImage } : {})}
                    onRemove={() => removeImage(imageIndex)}
                    onAltChange={(alt) =>
                      setDraft((current) => ({
                        ...current,
                        images: current.images.map((row, i) =>
                          i === imageIndex ? { ...row, alt } : row,
                        ),
                      }))
                    }
                    onTypeChange={(type) =>
                      setDraft((current) => ({
                        ...current,
                        images: current.images.map((row, i) =>
                          i === imageIndex ? { ...row, type } : row,
                        ),
                      }))
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--ink-3)]">
                {isCreate
                  ? 'Upload images here, or add an Image block in the body.'
                  : 'No images on this article yet.'}
              </p>
            )}
          </div>

          <div className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-4">
            <div className={`${eyebrowClass} mb-2`}>This article targets</div>
            {isCreate ? (
              <input
                className={editFieldClass}
                value={article.targetKeyword}
                onChange={(event) => updateDraft({ targetKeyword: event.target.value })}
                placeholder="Target keyword (required)…"
              />
            ) : (
              <div className="text-[15px] font-semibold text-[var(--ink-1)]">
                &ldquo;{article.targetKeyword}&rdquo;
              </div>
            )}
            <div className="mt-1 text-[12px] text-[var(--ink-3)]">
              {article.volume > 0
                ? `${article.volume.toLocaleString()} searches/mo`
                : 'Search volume not available'}
              {article.url ? ` · ${article.url}` : ' · Not published yet'}
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
        >
          <Icon kind="left" size={14} stroke={2} />
          Back to Articles
        </button>
        <span className="text-[12px] text-[var(--ink-4)]">
          {isCreate
            ? 'Add any blocks you need — nothing is prefilled'
            : 'Article generated by ELVA based on local keyword analysis'}
        </span>
      </div>
    </div>
  );
}
